import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Button, Card, Spinner, useConfirm } from '@tkpod/ui';
import { customSizeConfigSchema, isLegacyOptionType } from '@tkpod/shared';
import { api } from '@/lib/api';
import { ruleSentence } from './DisplayRulesEditor';
import { optionActionFailure, optionActionMessage, type OptionActionFailure } from './option-action-message';
import { toast } from '@/lib/toast';
import { OptionEditorModal, type PricingBlank } from './OptionEditorModal';
import { OptionsBlockConfigCard } from './OptionsBlockConfigCard';
import { OptionsLivePreview, type PreviewAxis } from './OptionsLivePreview';
import {
  choicesOf,
  rowFromPayload,
  rulesOf,
  type OptionWritePayload,
  type ProductOptionRow,
} from './product-option-model';

interface ReadinessItem {
  code: string;
  level: 'ok' | 'warning' | 'error';
  message: string;
  surface?: 'SHARED' | 'STOREFRONT';
  link: 'VARIANTS' | 'PRINT_AREAS' | 'SETTINGS' | 'OPTIONS' | null;
}

interface UptAxis {
  id: string;
  optionTypeId: string;
  resolvedDisplayName: string;
  uiType: string;
  isRequired: boolean;
  isActive: boolean;
  values: Array<{
    slug: string;
    displayName: string;
    name: string;
    swatchUrl: string | null;
    isActive: boolean;
  }>;
}

const LINK_TAB: Record<string, string> = {
  VARIANTS: 'variants',
  PRINT_AREAS: 'sides',
  OPTIONS: 'options',
};

export function ProductOptionsTab({
  templateId,
  productType,
  designerMode,
  pricingBlanks = [],
  onOpenTab,
  onDirtyChange,
}: {
  templateId: string;
  productType: string;
  designerMode: string;
  pricingBlanks?: PricingBlank[];
  onOpenTab: (tab: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState<ProductOptionRow | null | undefined>(undefined);
  const [editorDirty, setEditorDirty] = useState(false);
  const [serverFailure, setServerFailure] = useState<OptionActionFailure | null>(null);
  const [blockDirty, setBlockDirty] = useState(false);
  const [draftPreview, setDraftPreview] = useState<ProductOptionRow | null>(null);

  const optionsQuery = useQuery({
    queryKey: ['product-options', templateId],
    queryFn: () =>
      api.get<ProductOptionRow[]>(`/api/v1/admin/catalog/templates/${templateId}/options`),
  });
  const readinessQuery = useQuery({
    queryKey: ['product-options-readiness', templateId],
    queryFn: () =>
      api.get<{ items: ReadinessItem[]; blocking: boolean; portalBlocking?: boolean }>(
        `/api/v1/admin/catalog/templates/${templateId}/options/readiness`,
      ),
  });
  const reconcileQuery = useQuery({
    queryKey: ['product-options-reconcile', templateId],
    queryFn: () =>
      api.get<{ items: ReadinessItem[] }>(
        `/api/v1/admin/catalog/templates/${templateId}/options/reconcile`,
      ),
  });
  const axesQuery = useQuery({
    queryKey: ['upt-options', templateId],
    queryFn: () => api.get<UptAxis[]>(`/api/v1/admin/options/upt/${templateId}`),
  });
  const settingsQuery = useQuery({
    queryKey: ['admin', 'product-options', 'settings'],
    queryFn: () =>
      api.get<{ data: { blockEnabled: boolean; surchargeMode: string } }>(
        '/api/v1/admin/product-options/settings',
      ),
  });
  const blockQuery = useQuery({
    queryKey: ['product-options-block', templateId],
    queryFn: () => api.get<unknown>(`/api/v1/admin/catalog/templates/${templateId}/options-block-config`),
  });

  useEffect(() => {
    onDirtyChange?.(editorDirty || blockDirty);
  }, [editorDirty, blockDirty, onDirtyChange]);

  const options = optionsQuery.data ?? [];
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['product-options', templateId] });
    void queryClient.invalidateQueries({ queryKey: ['product-options-readiness', templateId] });
    void queryClient.invalidateQueries({ queryKey: ['product-options-reconcile', templateId] });
  };

  const save = useMutation({
    mutationFn: (input: { id?: string; payload: OptionWritePayload }) =>
      input.id
        ? api.put(`/api/v1/admin/catalog/templates/${templateId}/options/${input.id}`, input.payload)
        : api.post(`/api/v1/admin/catalog/templates/${templateId}/options`, input.payload),
    onSuccess: () => {
      setEditing(undefined);
      setDraftPreview(null);
      setEditorDirty(false);
      setServerFailure(null);
      invalidate();
      toast('Option saved.', 'success');
    },
    onError: (err: Error) => {
      const failure = optionActionFailure(err, 'Could not save the option.');
      setServerFailure(failure);
      toast(failure.message, 'error');
    },
  });

  const setActive = useMutation({
    mutationFn: (input: { id: string; isActive: boolean }) =>
      api.patch(`/api/v1/admin/catalog/templates/${templateId}/options/${input.id}/active`, {
        isActive: input.isActive,
      }),
    onSuccess: invalidate,
    onError: (err: Error) => toast(optionActionMessage(err, 'Could not update the option.'), 'error'),
  });

  const optionsKey = ['product-options', templateId] as const;
  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.post(`/api/v1/admin/catalog/templates/${templateId}/options/reorder`, { orderedIds }),
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: optionsKey });
      const previous = queryClient.getQueryData<ProductOptionRow[]>(optionsKey);
      queryClient.setQueryData<ProductOptionRow[]>(optionsKey, (current) => {
        if (!current) return current;
        const byId = new Map(current.map((option) => [option.id, option]));
        return orderedIds.flatMap((id) => {
          const option = byId.get(id);
          return option ? [option] : [];
        });
      });
      return { previous };
    },
    onError: (err: Error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(optionsKey, context.previous);
      toast(optionActionMessage(err, 'Could not reorder options.'), 'error');
    },
    onSettled: invalidate,
  });

  const duplicate = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/v1/admin/catalog/templates/${templateId}/options/${id}/duplicate`, {}),
    onSuccess: () => {
      invalidate();
      toast('Option duplicated.', 'success');
    },
    onError: (err: Error) => toast(optionActionMessage(err, 'Could not duplicate the option.'), 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api.delete(`/api/v1/admin/catalog/templates/${templateId}/options/${id}`),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<ProductOptionRow[]>(optionsKey, (current) =>
        current?.filter((option) => option.id !== id),
      );
      invalidate();
      toast('Option removed.', 'success');
    },
    onError: (err: Error) => toast(optionActionMessage(err, 'Could not remove the option.'), 'error'),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = options.findIndex((option) => option.id === active.id);
    const newIndex = options.findIndex((option) => option.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    reorder.mutate(arrayMove(options, oldIndex, newIndex).map((option) => option.id));
  };

  const axes = (axesQuery.data ?? []).map((axis) => ({
    optionTypeId: axis.optionTypeId,
    label: axis.resolvedDisplayName,
    valueCount: axis.values.length,
    values: axis.values
      .filter((value) => value.isActive)
      .map((value) => ({ key: value.slug, label: value.displayName || value.name })),
  }));
  const previewAxes: PreviewAxis[] = (axesQuery.data ?? []).map((axis) => ({
    optionTypeId: axis.optionTypeId,
    name: axis.resolvedDisplayName,
    uiType: axis.uiType,
    isRequired: axis.isRequired,
    isActive: axis.isActive,
    values: axis.values,
  }));
  const previewOptions = draftPreview
    ? options.some((option) => option.id === draftPreview.id)
      ? options.map((option) => (option.id === draftPreview.id ? draftPreview : option))
      : [...options, draftPreview]
    : options;

  if (optionsQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    );
  }

  if (optionsQuery.isError) {
    return (
      <Card>
        <div className="space-y-3 px-6 py-4">
          <p className="text-sm text-danger-700">
            {optionsQuery.error instanceof Error
              ? optionsQuery.error.message
              : 'Could not load options.'}
          </p>
          <Button size="sm" variant="outline" onClick={() => void optionsQuery.refetch()}>
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem]">
      {dialog}
      <div className="space-y-6">
        {settingsQuery.data?.data.blockEnabled === false ? (
          <p className="rounded-md border border-warning-border bg-warning-muted p-4 text-sm text-warning">
            Custom options are disabled platform-wide. Customers keep the theme add to cart until the block is turned on in settings.
          </p>
        ) : null}
        <Card>
          <div className="space-y-2 px-6 py-4">
            <h3 className="text-sm font-semibold text-text-primary">Readiness</h3>
            {readinessQuery.data &&
            readinessQuery.data.portalBlocking === false &&
            readinessQuery.data.blocking ? (
              <p className="text-sm text-text-secondary">
                Portal orders charge this price from the blank. Items marked Shopify publish block the storefront.
              </p>
            ) : null}
            {(readinessQuery.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-text-secondary">No blocking issues.</p>
            ) : (
              readinessQuery.data?.items.map((item) => (
                <div key={`${item.code}:${item.message}`} className="flex items-start justify-between gap-3 text-sm">
                  <span>
                    <Badge variant={item.level === 'error' ? 'danger' : 'warning'} size="sm">
                      {item.level}
                    </Badge>{' '}
                    {item.surface === 'STOREFRONT' ? (
                      <span className="text-text-tertiary">Shopify publish. </span>
                    ) : null}
                    {item.message}
                  </span>
                  {item.link === 'SETTINGS' ? (
                    <Link to="/settings/product-options" className="text-brand-600 hover:underline">
                      Fix
                    </Link>
                  ) : item.link && LINK_TAB[item.link] ? (
                    <button
                      type="button"
                      className="text-brand-600 hover:underline"
                      onClick={() => onOpenTab(LINK_TAB[item.link!]!)}
                    >
                      Fix
                    </button>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </Card>

        <Card>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-text-primary">Variant axes</h3>
              <button type="button" className="text-sm text-brand-600 hover:underline" onClick={() => onOpenTab('variants')}>
                Edit in Variants
              </button>
            </div>
            {(axesQuery.data ?? []).length === 0 ? (
              <p className="mt-2 text-sm text-text-secondary">No variant axes yet.</p>
            ) : (
              <ul className="mt-3 divide-y divide-border text-sm">
                {(axesQuery.data ?? []).map((axis) => (
                  <li key={axis.id} className="flex items-center justify-between py-2">
                    <span>{axis.resolvedDisplayName}</span>
                    <span className="text-text-secondary">
                      {axis.uiType.replace(/_/g, ' ')} · {axis.values.length} values
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {(reconcileQuery.data?.items.length ?? 0) > 0 && (
          <Card>
            <div className="space-y-2 px-6 py-4">
              <h3 className="text-sm font-semibold text-text-primary">Reconciliation</h3>
              {reconcileQuery.data?.items.map((item) => (
                <p key={item.code} className="text-sm text-text-secondary">{item.message}</p>
              ))}
            </div>
          </Card>
        )}

        <Card>
          <div className="flex items-center justify-between px-6 py-4">
            <h3 className="text-sm font-semibold text-text-primary">Additional options</h3>
            <Button size="sm" onClick={() => { setServerFailure(null); setEditing(null); }}>Add option</Button>
          </div>
          {options.length === 0 ? (
            <p className="px-6 pb-4 text-sm text-text-secondary">No custom options yet.</p>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={options.map((option) => option.id)} strategy={verticalListSortingStrategy}>
                <ul className="divide-y divide-border">
                  {options.map((option) => (
                    <OptionRow
                      key={option.id}
                      option={option}
                      options={options}
                      axes={axes}
                      onEdit={() => { setServerFailure(null); setEditing(option); }}
                      onToggle={() => setActive.mutate({ id: option.id, isActive: !option.isActive })}
                      onDuplicate={() => duplicate.mutate(option.id)}
                      onDelete={async () => {
                        const ok = await confirm({
                          title: 'Remove option',
                          description:
                            'This option will be removed from the product. Files already uploaded stay on their orders.',
                          confirmLabel: 'Remove',
                          tone: 'danger',
                        });
                        if (ok) remove.mutate(option.id);
                      }}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </Card>

        <OptionsBlockConfigCard
          templateId={templateId}
          unitCents={previewUnitCents(optionsQuery.data, pricingBlanks)}
          onDirtyChange={setBlockDirty}
        />
      </div>
      <div className="lg:sticky lg:top-4 lg:self-start">
        <OptionsLivePreview
          templateId={templateId}
          options={previewOptions}
          axes={previewAxes}
          blockConfig={blockQuery.data ?? {}}
          surchargeMode={settingsQuery.data?.data.surchargeMode ?? 'DISABLED'}
          blockEnabled={settingsQuery.data?.data.blockEnabled ?? true}
          pricingBlanks={pricingBlanks}
        />
      </div>
      {editing !== undefined && (
        <OptionEditorModal
          key={editing?.id ?? 'new'}
          open
          initial={editing}
          axes={axes}
          siblings={options
            .filter((option) => option.id !== editing?.id)
            .map((option) => ({
              key: option.key,
              displayName: option.displayName,
              optionType: option.optionType,
              choices: choicesOf(option.values).map((choice) => ({ key: choice.key, label: choice.label })),
            }))}
          saving={save.isPending}
          serverFailure={serverFailure}
          surchargeEnabled={settingsQuery.data?.data.surchargeMode !== 'DISABLED'}
          pricingBlanks={pricingBlanks}
          productType={productType}
          designerMode={designerMode}
          onDirtyChange={setEditorDirty}
          onDraft={(payload) => setDraftPreview(rowFromPayload(editing, payload))}
          onClose={() => {
            setEditing(undefined);
            setDraftPreview(null);
            setEditorDirty(false);
            setServerFailure(null);
          }}
          onSave={(payload) => save.mutate({ id: editing?.id, payload })}
        />
      )}
    </div>
  );
}

function OptionRow({
  option,
  options,
  axes,
  onEdit,
  onToggle,
  onDuplicate,
  onDelete,
}: {
  option: ProductOptionRow;
  options: ProductOptionRow[];
  axes: { optionTypeId: string; label: string; values: { key: string; label: string }[] }[];
  onEdit: () => void;
  onToggle: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const sortable = useSortable({ id: option.id });
  const rules = rulesOf(option.displayRules);
  const sentence = rules
    .map((rule) =>
      ruleSentence(
        rule,
        options
          .filter((item) => item.id !== option.id)
          .map((item) => ({
            key: item.key,
            displayName: item.displayName,
            optionType: item.optionType,
            choices: choicesOf(item.values).map((choice) => ({ key: choice.key, label: choice.label })),
          })),
        axes,
      ),
    )
    .join(' ');
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };
  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className="flex items-center gap-3 px-6 py-3"
    >
      <button type="button" className="cursor-grab text-text-tertiary" {...sortable.attributes} {...sortable.listeners}>
        ::
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-text-primary">{option.displayName}</span>
          <Badge size="sm">{option.role === 'VARIANT_SELECTOR' ? 'Variant selector' : 'Add-on'}</Badge>
          <Badge size="sm" variant="default">{option.optionType.replace(/_/g, ' ')}</Badge>
          {option.isRequired ? <Badge size="sm">Required</Badge> : null}
          {!option.isActive ? <Badge size="sm" variant="warning">Disabled</Badge> : null}
          {rules.length > 0 ? (
            <span title={sentence}>
              <Badge size="sm">Conditional</Badge>
            </span>
          ) : null}
          {isLegacyOptionType(option.optionType) ? <Badge size="sm" variant="warning">Legacy</Badge> : null}
        </div>
        <p className="text-xs text-text-secondary">{option.key}</p>
      </div>
      <Button size="sm" variant="outline" onClick={onToggle}>{option.isActive ? 'Disable' : 'Enable'}</Button>
      <Button size="sm" variant="outline" onClick={onEdit}>Edit</Button>
      <Button size="sm" variant="outline" onClick={onDuplicate}>Duplicate</Button>
      <Button size="sm" variant="outline" onClick={onDelete}>Delete</Button>
    </li>
  );
}

function previewUnitCents(
  options: ProductOptionRow[] | undefined,
  blanks: PricingBlank[],
): number | null {
  const priced = options?.find((option) => {
    if (option.optionType !== 'CUSTOM_SIZE' || !option.isActive) return false;
    const parsed = customSizeConfigSchema.safeParse(option.config ?? {});
    return parsed.success && Boolean(parsed.data.pricingVariantId);
  });
  if (!priced) return null;
  const parsed = customSizeConfigSchema.safeParse(priced.config ?? {});
  const blankId = parsed.success ? parsed.data.pricingVariantId : null;
  const blank = blanks.find((item) => item.id === blankId);
  if (!blank) return null;
  const cents = Math.round(Number(blank.portalUnitCost ?? blank.cost) * 100);
  return Number.isFinite(cents) && cents > 0 ? cents : null;
}
